/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import _ from 'lodash'
import moment from 'moment'
import { FsiLifecycleState, FsiType } from '../constants/fsi'
import { useServiceContext } from '../root'
import { TYPE } from '../screens/settings/properties/Actions/EditFormConstants'
import { convertDateForServer } from '../utils/dateFormatting'
import { convertTimestampToUTCMoment } from '../utils/dateUtils'
import useEntitySearchService from './useEntitySearchService'
import type { Metadata } from '../services/api/metadata'

interface WorkflowReference {
  formattedId: string
  id: number
}

interface FileElement {
  type: string
  workflowNo?: string
  workflowId?: number
  workflow?: WorkflowReference[]
  updatedOn?: number
  updatedAt?: number
  acl?: any
  gridItemClass?: string
  [key: string]: any
}

interface SearchResponse {
  elements: FileElement[]
  hasNextPage: boolean
  totalElements: number
}

interface MetadataCriterion {
  metadataTemplateId?: number
  property?: Metadata & { plain?: () => Metadata }
  condition: string
  value: any
  includeInherited?: boolean
}

interface FsiNameFilter {
  condition: string
  value: string
}

interface FsiTypeTemplate {
  value: number
  label: string
}

interface Tag {
  value: number
  label: string
}

interface Folder {
  id: number
  [key: string]: any
}

interface FilterFormValues {
  filename?: string
  filenameCondition?: string
  format?: string
  mimetype?: string
  lifecycleState?: string
  daysBeforeExpiration?: number
  beforeExpiration?: boolean
  fsiNameFilter?: FsiNameFilter
  fsiTypeTemplate?: FsiTypeTemplate
  fsiTypeTemplateId?: number
  tags?: Tag[]
  tagIds?: number[]
  folder?: Folder
  folderId?: number
  metadataCriteria?: MetadataCriterion[]
  [key: string]: any
}

interface QueryParams {
  pageStart?: number
  pageSize?: number
  sortBy?: string
  sortDesc?: boolean
  [key: string]: any
}

function usePropertySearchService(isStagingArea: boolean = false, studyContextId: string | number | null = null) {
  const context = useServiceContext()
  const { api } = context
  const { getLinkedEntities } = useEntitySearchService()

  const loadData = (queryParams: QueryParams, filter: FilterFormValues): Promise<SearchResponse> => {
    const promise = new Promise<SearchResponse>((resolve, reject) => {
      const filterParams = assembleFiltersFromFormValues({ ...filter })

      if (!isStagingArea) {
        api.files.propertySearch(filterParams, queryParams, true).then((data: any) => {
          let docsCounter = 1
          const filesList = _.filter(data.elements, function (item: FileElement) {
            if (item.workflowNo && item.workflowId) {
              item.workflow = [
                {
                  formattedId: item.workflowNo,
                  id: item.workflowId
                }
              ]
            }
            item.updatedOn = item.updatedAt

            if (item.acl?.level) {
              item.acl = item.acl.level
            }

            if (item.type !== FsiType.FOLDER) {
              if (docsCounter === 1) {
                item.gridItemClass = 'clear'
              }
              docsCounter++
            }
            return item.type !== FsiType.FOLDER
          })

          const foldersList = _.filter(data.elements, function (item: FileElement) {
            return item.type === FsiType.FOLDER
          })

          resolve({
            elements: [...foldersList, ...filesList],
            hasNextPage: data.hasNextPage,
            totalElements: data.totalElements
          })
        }, reject)
      } else {
        _.extend(filterParams, { envFsiId: studyContextId })
        api.etmf
          .propertySearch(studyContextId as string | number, queryParams, { filter: filterParams })
          .then((data: any) => {
            getLinkedEntities(data).then((dy: any) => {
              resolve({
                elements: dy.elements,
                hasNextPage: data.hasNextPage,
                totalElements: data.totalElements
              })
            })
          }, reject)
      }
    })

    return promise
  }

  const assembleFiltersFromFormValues = (originalValues: FilterFormValues): Record<string, any> => {
    const values = { ...originalValues }
    const metadataCriteria: any[] = []

    values?.metadataCriteria?.forEach((item) => {
      if (item.metadataTemplateId || (item.property && item.property.id)) {
        const filterRow: any = {
          metadataTemplateId: item.metadataTemplateId ? item.metadataTemplateId : item.property!.id,
          condition: item.condition,
          value: item.value,
          includeInherited: item.includeInherited
        }

        if (item.property && item.value) {
          switch (item.property.type) {
            case TYPE.SELECT_ONE_GROUP:
              filterRow.value = parseInt(item.value.replace('GROUP-', ''))
              break
            case TYPE.SELECT_ONE_USER:
              filterRow.value = parseInt(item.value.replace('USER-', ''))
              break
            case TYPE.SELECT_MULTI_GROUP:
              filterRow.value = item.value.map((i: string) => parseInt(i.replace('GROUP-', '')))
              break
            case TYPE.SELECT_MULTI_USER:
              filterRow.value = item.value.map((i: string) => parseInt(i.replace('USER-', '')))
              break
            case TYPE.DATETIME:
              if (moment.isMoment(item.value)) {
                filterRow.value = convertDateForServer(item.value)
              } else if (item.value instanceof Date) {
                filterRow.value = convertDateForServer(moment(item.value))
              } else if (item.value) {
                filterRow.value = parseInt(item.value)
              }
              break
            case TYPE.NUMBER:
              filterRow.value = parseInt(item.value)
              break
          }
        }
        metadataCriteria.push(filterRow)
      }
    })

    if (values.filename && values.filenameCondition) {
      values.fsiNameFilter = {
        condition: values.filenameCondition,
        value: values.filename
      }
    }

    if (_.includes([FsiType.DOCUMENT, FsiType.PLACEHOLDER, FsiType.REFERENCE], values.format)) {
      values.mimetype = undefined
    } else if (values.format) {
      values.mimetype = values.format
      values.format = undefined
    }

    if (values.lifecycleState === FsiLifecycleState.HELPER_EXPIRING) {
      values.beforeExpiration = true
      values.lifecycleState = undefined
      values.daysBeforeExpiration = values.daysBeforeExpiration ? values.daysBeforeExpiration : 0
    } else {
      values.daysBeforeExpiration = undefined
    }

    return _.omit(
      {
        ...values,
        metadataCriteria: metadataCriteria,
        fsiTypeTemplateId: values.fsiTypeTemplate ? values.fsiTypeTemplate.value : undefined,
        tagIds: values.tags ? values.tags.map((tag) => tag.value) : undefined,
        folderId: values.folder ? values.folder.id : undefined
      },
      'filename',
      'filenameCondition',
      'fsiTypeTemplate',
      'tags',
      'folder'
    )
  }

  const assembleFiltersToFormValues = async (filter: FilterFormValues): Promise<FilterFormValues> => {
    const assembledFilterValues: FilterFormValues = { ...filter, tags: [] }

    if (filter.folderId) {
      assembledFilterValues.folder = { id: filter.folderId }
    }

    assembledFilterValues.format = filter.format ? filter.format : filter.mimetype ? filter.mimetype : undefined

    assembledFilterValues.filename =
      filter.fsiNameFilter && filter.fsiNameFilter.value ? filter.fsiNameFilter.value : ''
    assembledFilterValues.filenameCondition =
      filter.fsiNameFilter && filter.fsiNameFilter.condition ? filter.fsiNameFilter.condition : undefined

    if (filter.daysBeforeExpiration) {
      assembledFilterValues.lifecycleState = FsiLifecycleState.HELPER_EXPIRING
      assembledFilterValues.daysBeforeExpiration = filter.daysBeforeExpiration
    }

    if (filter.fsiTypeTemplateId) {
      const docType = await context.api.documentTypes.getById(filter.fsiTypeTemplateId, false)
      assembledFilterValues.fsiTypeTemplate = {
        value: (docType as any).id,
        label: (docType as any).name
      }
    }

    if (Array.isArray(filter.tagIds)) {
      for (let i = 0; i < filter.tagIds.length; i++) {
        try {
          const tag = await context.api.tags.getTagById(filter.tagIds[i])
          assembledFilterValues.tags!.push({
            value: tag.id,
            label: tag.name
          })
        } catch (error) {
          // Skip tags that no longer exist or cannot be loaded
          console.warn(`Failed to load tag with ID ${filter.tagIds[i]}:`, error)
        }
      }
    }

    if (filter.metadataCriteria && filter.metadataCriteria.length) {
      assembledFilterValues.metadataCriteria = []
      for (let i = 0; i < filter.metadataCriteria.length; i++) {
        const property = (await context.api.metadata.getById(
          filter.metadataCriteria[i].metadataTemplateId!
        )) as Metadata
        let value = filter.metadataCriteria[i].value

        if (value) {
          switch (property?.type) {
            case TYPE.SELECT_ONE_GROUP:
              value = 'GROUP-' + value
              break
            case TYPE.SELECT_ONE_USER:
              value = 'USER-' + value
              break
            case TYPE.SELECT_MULTI_GROUP:
              value = value.map((i: number) => 'GROUP-' + i)
              break
            case TYPE.SELECT_MULTI_USER:
              value = value.map((i: number) => 'USER-' + i)
              break
            case TYPE.DATETIME:
              if (!_.includes(['NEXT_N_DAYS', 'PREVIOUS_N_DAYS'], filter.metadataCriteria[i].condition)) {
                value = convertTimestampToUTCMoment(value)
              }
          }
        }

        assembledFilterValues.metadataCriteria.push({
          property: property as Metadata & { plain?: () => Metadata },
          metadataTemplateId: filter.metadataCriteria[i].metadataTemplateId,
          condition: filter.metadataCriteria[i].condition,
          value: value,
          includeInherited: filter.metadataCriteria[i].includeInherited
        })
      }
    }

    return _.omit(
      assembledFilterValues,
      'folderId',
      'mimetype',
      'fsiNameFilter',
      'beforeExpiration',
      'fsiTypeTemplateId',
      'tagIds'
    ) as FilterFormValues
  }

  return {
    assembleFiltersFromFormValues,
    assembleFiltersToFormValues,
    loadData
  }
}

export default usePropertySearchService
